/**
 * Where a screen sends someone, named once.
 *
 * Two of these are destinations before they are screens. Naming them now is
 * what keeps a redirect from being written as a literal that nobody finds when
 * the screen it points at finally lands.
 */
export const paths = {
  /** The back-office entry point (FE004). Public — a signed-out route. */
  signIn: '/sign-in',

  /**
   * The must-change-credential gate (FE007).
   *
   * A user whose `mustChangeCredential` is set comes here and not to the
   * dashboard: the API permits exactly one route until they replace the
   * credential somebody else chose for them, so a dashboard redirect would put
   * them in front of a screen where everything refuses.
   *
   * It is a real screen now, declared `requireSignedIn({ allowsPendingCredential:
   * true })` — the only declaration the start-up audit accepts for this path,
   * because any other one would have the guard send a pending user here and this
   * route turn them straight back round.
   */
  changeCredential: '/change-credential',

  /** Where an ordinary sign-in lands. FE006 owns the real signed-in shell. */
  dashboard: '/',

  /**
   * The first screen of park setup (FE008): the outlets, and creating one.
   *
   * `OWNER`-only, mirroring the API — the whole identity-admin surface carries
   * `@RequireRole('OWNER')` there and this is the first screen of it.
   */
  outlets: '/outlets',

  /**
   * The devices the park takes money on (FE010): the terminals, and creating
   * one.
   *
   * `OWNER`-only, mirroring the API — `TerminalsController` carries
   * `@RequireRole('OWNER')` on the class, so every operation behind this screen
   * refuses anybody else.
   */
  terminals: '/terminals',

  /**
   * The stand-alone billing machines at the ride and activity counters (#228),
   * whose exports are uploaded as cash sales.
   *
   * `OWNER` and `MANAGER` read; registering and moving a machine are owner-only
   * on the API, so the screen hides those two controls from a manager.
   */
  billingMachines: '/billing-machines',

  /** One machine and its attachment history (#228). No `nav` entry. */
  billingMachine: '/billing-machines/:id',

  /**
   * One machine's product codes and the catalogue items they mean (#229).
   * Reached from the machine; no `nav` entry. Takes `?code=&name=` to open the
   * mapping form prefilled, which is how an upload's `UNMAPPED` refusal links here.
   */
  billingMachineProductCodes: '/billing-machines/:id/product-codes',

  /**
   * The park's people, and creating one (FE012).
   *
   * `OWNER`-only, mirroring the API: `UsersController` carries
   * `@RequireRole('OWNER')` on every operation, and CL-104 is explicit that the
   * manager role does not manage users.
   */
  users: '/users',

  /**
   * Which outlets a person may work at (FE014).
   *
   * `OWNER`-only, mirroring the API: `UserAssignmentsController` and
   * `OutletAssignmentsController` both carry `@RequireRole('OWNER')` on the
   * class, and the `PUT` behind this screen says so in its own document —
   * "AUTH_FORBIDDEN — only the owner staffs the park".
   */
  staffing: '/staffing',

  /**
   * What the garden sells, and creating one (FE020).
   *
   * `OWNER` and `MANAGER`, which mirrors the API rather than narrowing it:
   * `CatalogueItemsController` carries `@RequireRole('OWNER')` on the class and
   * the two reads step out of it deliberately, citing FR-232 — "a manager must
   * be able to read". The writes stay owner-only, so the create is a control
   * this screen hides rather than a route it refuses.
   */
  catalogue: '/catalogue',

  /**
   * Creating the whole catalogue from a file (FE208).
   *
   * `OWNER` and nothing wider, which is **narrower than the catalogue list above
   * it** and mirrors the write rather than the read. Every request this screen
   * makes is `POST /v1/catalogue/items`, which inherits
   * `CatalogueItemsController`'s `@RequireRole('OWNER')` and states the reason
   * itself — "only the owner stocks the catalogue (CL-206)". A manager admitted
   * here would get a review table and a button that 403s five hundred times.
   *
   * No `nav` entry: it is an action on the catalogue rather than a destination,
   * reached from the Catalogue screen. `NavLink` matches descendants, so
   * Catalogue stays lit while this is open — which is where somebody on it
   * actually is.
   *
   * Sits above `/catalogue/items/:id/…` and cannot be confused with one: those
   * are `/catalogue/items/…` and this is a literal second segment.
   */
  catalogueUpload: '/catalogue/upload',

  /**
   * Every price one catalogue item has ever carried, and the way to set the next
   * one (FE022).
   *
   * `OWNER` and nothing wider, which is **narrower than the catalogue list above
   * it** and mirrors the API exactly. All three operations behind this screen
   * inherit `CataloguePricesController`'s `@RequireRole('OWNER')`, and the
   * document says why on each of them in as many words: "cost price is the
   * owner's alone (CL-207, FR-233)". Every row of this history carries
   * `costPaise`, so the guard belongs at the route rather than on fields inside a
   * screen anybody can open — there is no version of this screen with the cost
   * column taken out that would be safe for a manager, because the reads
   * themselves are refused.
   *
   * The first parameterised route in the app. `cataloguePricesPath` below is how
   * a screen builds one; nothing interpolates this template by hand.
   *
   * No `nav` entry — it is about *one* item and there is no such thing as the
   * price history in general. It is reached from that item's amount in the
   * catalogue list, which is shown as a link only to an owner.
   */
  cataloguePrices: '/catalogue/items/:id/prices',

  /**
   * Which outlets may sell one catalogue item, and the way to change the set
   * (FE023).
   *
   * `OWNER` and nothing wider, which is **narrower than the catalogue list** and
   * mirrors the write rather than the read. `CatalogueAvailabilityController`
   * carries `@RequireRole('OWNER')` on the class and says why — "only the owner
   * sets availability (CL-206)" — and the `GET` beside it steps out to
   * `@RequireRole()` + `@RequireOutletScope()` so a till can read what it sells.
   * This screen is the write: everything on it is a control that sets the set,
   * and a manager admitted here would get a table of checkboxes whose Save is
   * refused. The reverse direction below is where a manager reads availability.
   *
   * No `nav` entry — it is about *one* item. It is reached from that item in the
   * catalogue, and from the outlet catalogue's rows.
   */
  catalogueAvailability: '/catalogue/items/:id/outlets',

  /**
   * What one outlet sells (FE023): the back-office twin of what a till holds.
   *
   * `OWNER` and `MANAGER`, matching the catalogue list rather than the outlets
   * list it hangs off. `GET /v1/outlets/{id}/catalogue` is `@RequireRole()` +
   * `@RequireOutletScope()`, so the API admits outlet and gate staff to their own
   * outlets too; this stops at manager for the reason the catalogue entry gives —
   * till staff have their own shell, and widening the back office to them is a
   * decision for the ticket that builds one.
   *
   * It is deliberately wider than `paths.outlets` above it, which is `OWNER`-only
   * and is not on the way here: a manager reaches this from the catalogue's
   * "Sold at" filter, which is the question this screen answers in full.
   *
   * No `nav` entry — it is about *one* outlet.
   */
  outletCatalogue: '/outlets/:id/catalogue',

  /**
   * The park's GST registrations — each one's registered name, address, GSTIN
   * and invoice title, what an outlet trading under it prints at the top of a
   * tax invoice (#321, blossom-rest-api#534). `OWNER` and `MANAGER` read it,
   * mirroring `GET /v1/tax-identities`; only an owner may save, mirroring `PUT`.
   */
  taxIdentities: '/tax-identities',

  /**
   * The day's tickets (FE027): what the gates and the outlets have issued.
   *
   * `OWNER` and `MANAGER`, matching the catalogue rather than the owner-only
   * setup screens. `TicketsController` declares `@RequireRole()` with no
   * arguments and `@RequireOutletScope()`, and says why in as many words: FR-322
   * is about *reach* rather than rank, so the difference between an owner and a
   * cashier is the filter composed into the statement rather than a list of
   * roles. This stops at manager for the reason the catalogue entry gives — till
   * staff sign in with a PIN at a counter and the back-office shell is not their
   * shell — and narrower is the recoverable direction.
   *
   * Nothing on the screen writes: voiding a ticket is `POST
   * /v1/tickets/{id}/void` and belongs to its own ticket.
   */
  tickets: '/tickets',

  /**
   * One ticket's record (FE028): what a visitor disputing a charge is shown.
   *
   * `OWNER` and `MANAGER`, the same declaration as the list it hangs off and for
   * the same reasons — `TicketsController` declares `@RequireRole()` and
   * `@RequireOutletScope()` on all three of its reads, so reach is a conjunct of
   * the `WHERE` clause rather than a list of roles, and this stops at manager
   * because till staff have their own shell.
   *
   * The address is the ticket's **id** and not its printed number, although the
   * number is what somebody types. The two are different things: a number is
   * `(terminal, counter, value)` and needs a counter kind to be unique, while
   * the id names one row for good. Looking up by number resolves to this, which
   * is what makes the result a page that can be refreshed, kept open beside a
   * queue, and read out to somebody else.
   *
   * No `nav` entry — it is about *one* ticket. `NavLink` matches descendants, so
   * Tickets stays lit while this is open, which is where somebody on it is.
   */
  ticket: '/tickets/:id',

  /**
   * The day's sales (FE032): what the outlets have taken.
   *
   * `OWNER` and `MANAGER`, the same declaration as the tickets list and for the
   * same reasons. `SalesController` declares `@RequireRole()` with no arguments
   * and `@RequireOutletScope()` on all three of its reads (R-412), so reach is a
   * conjunct of the `WHERE` clause rather than a list of roles — an owner and a
   * manager see every outlet, outlet staff see their assignments. This stops at
   * manager because till staff sign in with a PIN at a counter and the
   * back-office shell is not their shell, and narrower is the recoverable
   * direction.
   *
   * Nothing on the screen writes: refunding and reprinting are `POST
   * /v1/sales/{id}/refund` and `/reprint`, and both belong to FE034.
   */
  sales: '/sales',

  /**
   * One sale's record (FE033): what a customer disputing a charge is shown — the
   * basket line by line, the payment, both clocks, and the refund if there is one.
   *
   * `OWNER` and `MANAGER`, the same declaration as the list it hangs off and for
   * the same reasons — `SalesController` declares `@RequireRole()` and
   * `@RequireOutletScope()` on all three of its reads (R-412), so reach is a
   * conjunct of the `WHERE` clause rather than a list of roles, and this stops at
   * manager because till staff have their own shell.
   *
   * The address is the sale's **id** and not its receipt number, although the
   * number is what somebody types. The two are different things, and here the
   * difference bites harder than it does for a ticket: a receipt number is
   * `(terminal, counter, value)` and the counter is the *till's* (R-402), so two
   * tills can each have printed a `482` and a number alone names as many sales as
   * there are tills in reach. The id names one row for good. Looking up by number
   * resolves to this — with a choice in between where the number was ambiguous —
   * which is what makes the result a page that can be refreshed, kept open beside
   * a queue, and read out to somebody else.
   *
   * Nothing on it writes. A recorded sale is never mutated, so there is no edit
   * affordance to guard; refunding and reprinting are `POST /v1/sales/{id}/refund`
   * and `/reprint`, and both belong to FE034.
   */
  sale: '/sales/:id',

  /**
   * Bills imported from the stand-alone billing machines (#231), and one of
   * them. `OWNER` and `MANAGER`, mirroring `MachineBillsController`. Imported
   * sales are not till sales and `GET /v1/sales` never lists them, so they get
   * their own list rather than a filter on the sales one. `?importId=` narrows
   * the list to one upload — see `machineBillsForImportPath`.
   */
  machineBills: '/machine-bills',

  /**
   * Billing machine uploads (#230): the history, the upload flow and one
   * import's record. `OWNER` and `MANAGER`, mirroring `MachineImportsController`.
   * `/new` is a static segment, so the router ranks it above `:id`.
   */
  machineUploads: '/machine-uploads',
  machineUpload: '/machine-uploads/new',
  machineImport: '/machine-uploads/:id',
  machineBill: '/machine-bills/:id',

  /**
   * What an outlet holds, per item (FE036) — and what it held at a moment that
   * has passed.
   *
   * `requireSignedIn()` and no role, which mirrors the API rather than narrowing
   * it: `GET /v1/outlets/{outletId}/stock` is `@RequireRole()` with no arguments
   * plus `@RequireOutletScope()`, so reach is a conjunct of the query rather than
   * a rank. The three routes that *change* a level are `@RequireRole('OWNER',
   * 'MANAGER')` and belong to other tickets; nothing on this screen writes.
   *
   * This is **wider than the other back-office screens deliberately**, and it is
   * the first entry in this file that does not stop at manager. The reasoning the
   * catalogue and sales entries give — till staff sign in with a PIN and the back
   * office is not their shell — is about screens whose *whole surface* is
   * administration. This one is a shelf count, the epic ships no till screen for
   * it, and a guard stricter than the server would hide it from somebody the API
   * would serve.
   *
   * No `:outletId` in the path, unlike `outletCatalogue` above. The outlet is a
   * filter in the rail so this can carry a navigation entry; the screen says why
   * at length.
   */
  stock: '/stock',

  /**
   * Why a level is what it is (FE040): the movement log, newest first.
   *
   * `requireSignedIn()` and no role, for the same reason and by the same mirror
   * as `stock` above — `StockMovementsController.list` is `@RequireRole()` with
   * no arguments plus `@RequireOutletScope()`. Nothing on it writes.
   *
   * **Deliberately `/stock-movements` and not `/stock/movements`**, which is the
   * path this would have taken if it were only a matter of reading well. Both
   * screens carry a navigation entry, `NavLink` matches descendants unless it is
   * told not to, and a nested path would therefore light **two** entries at once
   * and set `aria-current="page"` on both — announcing two current pages to
   * anybody navigating by landmark. The alternative was passing `end` to every
   * link in `BackOfficeShell`, which changes the behaviour of eight entries that
   * are relying on it: `/catalogue/items/:id/prices` keeps Catalogue lit and
   * `/sales/:id` keeps Sales lit, both on purpose, because a screen reached from
   * a list is somewhere *inside* that section. This screen is not inside Stock —
   * it is the second half of it, standing beside the levels — so the flat path
   * says what is true and costs nothing.
   */
  stockMovements: '/stock-movements',

  /**
   * Recording what happened to the goods that was not a sale (FE038): broken,
   * spoiled, lost or found.
   *
   * `OWNER` and `MANAGER`, which mirrors the API and is **narrower than the two
   * stock reads beside it**: `CorrectionsController.record` carries
   * `@RequireRole('OWNER','MANAGER')` where `stock-levels` and `stock-movements`
   * carry `@RequireRole()`. All three are additionally
   * `@RequireOutletScope()`. The reads are wide because a cashier wanting to
   * know whether the doll shop has any dolls left has that screen or nothing;
   * this one writes, and the API says who writes.
   *
   * **Deliberately `/stock-corrections` and not `/stock/corrections`**, for the
   * reason `stockMovements` above sets out in full: `NavLink` matches
   * descendants, both screens carry a navigation entry, and a nested path would
   * light two entries at once and set `aria-current="page"` on both. This screen
   * is not inside the levels — it is one of the three acts that move them.
   */
  stockCorrections: '/stock-corrections',

  /**
   * Goods that arrived at an outlet (FE037): the delivery note, entered.
   *
   * `OWNER` and `MANAGER`, which mirrors the API and is **narrower than the two
   * stock reads**, for the reason `stockCorrections` above gives in full:
   * `DeliveriesController.record` carries `@RequireRole('OWNER', 'MANAGER')`
   * where `stock-levels` and `stock-movements` carry `@RequireRole()`, and all
   * of them carry `@RequireOutletScope()` besides. Reading what a shelf holds is
   * anybody's; saying that goods arrived on it is not.
   *
   * **`/stock-deliveries` and not `/stock/deliveries`**, matching its two
   * neighbours rather than the nesting an earlier version of this entry used.
   * The argument is `stockMovements`': this screen carries a navigation entry,
   * `NavLink` matches descendants, and a nested path would light both Stock and
   * Deliveries at once and set `aria-current="page"` on both. It is not inside
   * the levels — it is one of the three acts that move them, and the three now
   * read alike.
   */
  stockDeliveries: '/stock-deliveries',

  /**
   * Counting the shelf and recording what was found (FE039).
   *
   * `OWNER` and `MANAGER`, mirroring the API and narrower than the two stock
   * reads for the reason `stockCorrections` above gives:
   * `StocktakesController.record` carries `@RequireRole('OWNER','MANAGER')` plus
   * `@RequireOutletScope()`, where `stock-levels` and `stock-movements` carry
   * `@RequireRole()`. Reading a shelf is anybody's; settling one is a manager's.
   *
   * **`/stocktake` and not `/stock-stocktakes`**, which is where the pattern set
   * by `stockMovements` and `stockCorrections` would have landed it. Those two
   * carry the `stock-` prefix because "movements" and "corrections" name nothing
   * on their own; "stocktake" is one word that already contains its subject, and
   * `/stock-stocktakes` says it twice. The flat shape is the part that matters
   * and is kept: `NavLink` matches descendants, all five stock screens carry a
   * navigation entry, and a nested `/stock/…` would light two entries at once
   * and set `aria-current="page"` on both.
   */
  stocktake: '/stocktake',

  /**
   * Money that left an outlet for a reason that was not a refund or a till
   * payout, newest first (FE049): the expense log.
   *
   * `OWNER` and `MANAGER`, mirroring the API. `GET /v1/expenses` carries
   * `@RequireOutletScope()` and no role list at all, and `outletScopeFilter`
   * answers `undefined` for both of those roles (FR-111 as amended), so an owner
   * and a manager each see **every** outlet here rather than a subset. Stopping
   * at manager rather than following the scope all the way to
   * `requireSignedIn()` is the argument the catalogue and sales entries make:
   * outlet and gate staff sign in with a PIN at a counter and the back-office
   * shell is not their shell.
   *
   * **The first screen of Reporting**, and the flat path is the section's shape
   * rather than an accident — `stockMovements` above sets out why in full:
   * `NavLink` matches descendants, every screen carrying a navigation entry
   * would light two at once under a nested path, and `aria-current="page"` would
   * be announced on both. FE050, FE051 and FE052 take `/cash-book`,
   * `/profit-and-loss` and `/trading-summary` beside this one.
   */
  expenses: '/expenses',

  /**
   * Recording one (FE049): the only write in the Reporting epic.
   *
   * `OWNER` and `MANAGER`, mirroring `POST /v1/outlets/{outletId}/expenses`'s
   * own `@RequireRole('OWNER','MANAGER')` — the same pair as the log it is
   * reached from, so there is no version of this where somebody can read the
   * list and not record a row.
   *
   * **Nested under the log on purpose, which is the opposite of what the five
   * stock screens do**, and the difference is `nav`. Those five each carry a
   * navigation entry, so a nested path would light two at once; this one carries
   * none. It is reached from the action in the log's rail, and `NavLink`
   * matching descendants is then exactly what is wanted — Expenses stays lit
   * while somebody is recording one, which is where they actually are. Same
   * shape as `/catalogue/items/:id/prices` keeping Catalogue lit.
   */
  recordExpense: '/expenses/record',

  /**
   * The suppliers the park pays (#247), named on an expense. Park-wide, not per
   * outlet. `OWNER` and `MANAGER`, mirroring `VendorsController`'s
   * `@RequireRole('OWNER', 'MANAGER')` on the class. Flat, per the section's
   * shape — it carries its own navigation entry.
   */
  vendors: '/vendors',

  /**
   * The UPI each outlet received on a trading day, as declared (#258). `OWNER`
   * and `MANAGER`, mirroring both UPI routes: either may declare or correct.
   */
  upiDeclarations: '/upi-declarations',

  /**
   * Partners' percentages of an outlet's full collection (#258). `OWNER` and
   * `MANAGER` read; opening and closing an agreement are owner-only on the API,
   * so the screen hides those controls from a manager.
   */
  revenueShares: '/revenue-shares',

  /**
   * The park's daily sheet, one page of its book per day (#276). `OWNER` and
   * `MANAGER`, mirroring every read it makes, and the park expenses it records.
   */
  dailySheet: '/daily-sheet',

  /**
   * The park's one opening balance (#276). `OWNER` only, mirroring
   * `POST /v1/opening-balances`. No nav entry, so Daily sheet stays lit — the
   * same shape as `recordExpense` under Expenses.
   */
  dailySheetOpeningBalance: '/daily-sheet/opening-balance',

  /**
   * The sheet's blocks of outlets (#276). `OWNER` and `MANAGER` read it; only
   * the owner's `PUT` arranges it, so a manager gets it read-only. No nav entry.
   */
  dailySheetLayout: '/daily-sheet/layout',

  /**
   * One outlet's takings, refunds, spending and what is left (FE052): the
   * trading summary.
   *
   * `OWNER` and `MANAGER`, mirroring the API.
   * `GET /v1/reports/outlets/{outletId}/trading-summary` carries
   * `@RequireOutletScope()` and no role list, the same declaration the expense
   * log above carries, and `outletScopeFilter` answers `undefined` for both of
   * those roles — so a manager reads every outlet here rather than a subset.
   *
   * **This is the manager-readable half of a deliberate split, and the split is
   * exactly cost and margin.** The profit and loss beside it is
   * `@RequireRole('OWNER')` and its resource carries `costOfGoods`; this one is
   * the same nine fields without it, so including cost here would be a compile
   * error rather than a filter somebody can forget. Two screens with two
   * audiences, because the API is shaped that way and mirroring it is cheaper
   * than reconciling it.
   *
   * Flat beside `/expenses`, for the reason `stockMovements` above sets out in
   * full: `NavLink` matches descendants, every Reporting screen carries a
   * navigation entry, and a nested `/reports/…` would light two at once.
   */
  tradingSummary: '/trading-summary',

  /**
   * Revenue, what the goods cost, the margin that left and the net (FE051): the
   * park's profit and loss, one outlet's, and the CSV of the same period.
   *
   * **`requireRole('OWNER')`, and it is the only Reporting screen that is.**
   * `ProfitAndLossController.park`, `ProfitAndLossController.outlet` and
   * `ExportController.profitAndLossCsv` each declare `@RequireRole('OWNER')`, so
   * a manager is refused `403 AUTH_FORBIDDEN` at the routing layer before the
   * handler runs — there is no partial response and no field-level filtering.
   *
   * The rule behind the split is exactly cost and margin, which
   * `export.controller.ts` says in those words: this export carries both and
   * `cash-book.csv` carries neither. `/trading-summary` above is the report a
   * manager gets instead, and its resource omits `costOfGoods` at the type level
   * rather than filtering it out — so the two screens are not one screen with a
   * role flag, and neither entry may be copied onto the other.
   *
   * **The export is not a path of its own**, for the reason the cash book below
   * gives in full: it is an action over the period the screen is already
   * showing, and it produces a file rather than a view.
   *
   * Flat beside `/trading-summary`, per the section's shape — `NavLink` matches
   * descendants and every Reporting screen carries a navigation entry, so a
   * nested `/reports/…` would light two at once.
   */
  profitAndLoss: '/profit-and-loss',

  /**
   * Cashing up one outlet's trading day (FE046): the reconciliation, and the
   * four acts that change it.
   *
   * `requireRole('OWNER', 'MANAGER')`, mirroring the two **reads** behind it —
   * the day lookup and the reconciliation both admit that pair. It deliberately
   * does not mirror the acts, because **the acts do not agree with each other**:
   * closing is `OWNER` or `MANAGER`, and reopening is `OWNER` alone (CL-603).
   * A route declared on the narrower of the two would hide the whole
   * reconciliation from every manager in the park to protect one button, and one
   * declared on nothing at all is what `route-audit.ts` throws for. The reopen
   * control is hidden from a manager on the screen, and the server refuses it
   * regardless.
   *
   * Flat and not nested under `/outlets`. The outlet is a filter here rather
   * than the subject — a manager cashing up moves between outlets on one screen
   * — and the five stock screens settled the same question the same way.
   */
  dayClose: '/day-close',

  /**
   * Every entry that moved money, in the order it happened (FE050): the cash
   * book, and the CSV of the same period beside it.
   *
   * `OWNER` and `MANAGER`, the same pair as the expense log above and for the
   * same reason. Both `GET /v1/reports/cash-book` and
   * `GET /v1/reports/cash-book.csv` carry `@RequireOutletScope()` and no role
   * list, and `outletScopeFilter` answers `undefined` for both roles, so each
   * sees **every** outlet here rather than a subset.
   *
   * **The export is not a path of its own.** It is an action on this screen over
   * the period this screen is already showing, and it produces a file rather
   * than a view — a route that answered with a download and rendered nothing
   * would be a back button that lands on a blank page. `export.controller.ts`
   * makes the same distinction: the CSV is the same statement as this read, by
   * one builder with two serialisers, and not a second report.
   *
   * Flat, per the section's shape — see `expenses` above and `stockMovements`
   * for why every screen carrying a navigation entry stays off a nested path.
   */
  cashBook: '/cash-book',

  /**
   * Who recorded what, and which till it was recorded at (FE053).
   *
   * **`requireRole('OWNER')`**, mirroring both routes behind it:
   * `AttributionController.staff` and `AttributionController.terminals` each
   * declare `@RequireRole('OWNER')`, and a manager, an outlet staff member and a
   * gate staff member are all refused `403 AUTH_FORBIDDEN` before the handler
   * runs. There is no partial response and no field-level filtering, so anybody
   * admitted here who is not the owner would reach a screen that is nothing but
   * a refusal. The item-level reports are what the other roles read instead
   * (CL-901).
   *
   * **Do not infer this entry from the three `@RequireOutletScope()` Reporting
   * screens beside it.** The expense log, the cash book and the trading summary
   * are `OWNER, MANAGER` because `outletScopeFilter` answers `undefined` for
   * both; these two routes name a role, and the profit and loss is the other
   * entry in this section that does.
   *
   * Flat beside the rest of the section, per the shape `expenses` and
   * `stockMovements` set out: `NavLink` matches descendants and every Reporting
   * screen carries a navigation entry, so a nested `/reports/…` would light two
   * at once.
   */
  attribution: '/attribution',

  /**
   * One thing's whole timeline — a receipt, a ticket, a person's shift, an item
   * on a shelf (#187).
   *
   * `OWNER` and `MANAGER`, mirroring the API and not narrowing it:
   * `GET /v1/reports/trace` publishes `AUTH_FORBIDDEN` for "the caller is
   * authenticated but holds none of the roles this route admits (OWNER,
   * MANAGER)", which is the role list stated on the route itself rather than one
   * inferred from an outlet scope. It is the one investigation read a manager
   * can reach, and **the only Reporting route whose own declared list names
   * `MANAGER`**: the profit and loss and the attribution ranks each declare
   * `OWNER` alone, and the expense log, the cash book and the trading summary
   * reach the same pair through `outletScopeFilter` rather than by naming it.
   *
   * Flat, per the section's shape — see `expenses` and `stockMovements` for why
   * every screen carrying a navigation entry stays off a nested path.
   */
  trace: '/trace',

  /**
   * What each thing in the catalogue did over a period, and whether the shelf
   * agrees (#185).
   *
   * **`requireRole('OWNER')`**, mirroring both routes behind it:
   * `InvestigationController.parkItems` and `InvestigationController.outletItems`
   * each declare `@RequireRole('OWNER')`, and their `403` says so — a manager, an
   * outlet staff member and a gate staff member are all refused before either
   * handler runs. The reason is in the payload rather than only in the
   * decorator: every row carries `costPaise` and `marginPaise`, which is the same
   * line `profitAndLoss` above is drawn on.
   *
   * **Do not infer this from `trace` beside it.** That route's own declared list
   * names `MANAGER` and carries no cost or margin; these two do neither. Each
   * Reporting guard is read off the route it mirrors, never off its neighbour.
   *
   * One path for two routes. The park read and the per-outlet read answer the
   * same question at two scopes, and the scope is a filter on the screen rather
   * than a place a person navigates to — a URL per outlet would be a second
   * navigation entry for a picker.
   *
   * Flat, per the section's shape — see `expenses` and `stockMovements` for why
   * every screen carrying a navigation entry stays off a nested path.
   */
  items: '/items',

  /**
   * Item sales for a manager: Items without cost or margin (#339).
   *
   * `requireRole('MANAGER')`. Both routes behind it —
   * `ItemTradingController.park` and `.outlet` — admit `OWNER` and `MANAGER`,
   * and carry no cost or margin in their types. The owner is left out on this
   * side only so the owner keeps one entry, {@link items}, which is this and the
   * cost beside it.
   *
   * One path for two routes, as {@link items} does, and flat per the section's
   * shape.
   */
  itemSales: '/item-sales',

  /**
   * The park's item activity as a CSV (#188).
   *
   * `OWNER`-only, mirroring the API and for the reason it gives:
   * `ExportController.parkItemsCsv` and the `GET /v1/reports/items` read behind
   * it both carry `@RequireRole('OWNER')`, because every item row publishes
   * `costPaise`, `refundedCostPaise`, `voidedCostPaise` and `marginPaise` — the
   * park's buying position, per item, in a file. There is no manager-readable
   * item export and adding one is filed upstream as their #441.
   *
   * **A route of its own rather than a control on the items screen.** {@link
   * items} above is where this export belongs — an export button belongs on the
   * screen showing the thing exported — and it landed while this was in flight,
   * on a branch this one could not see. Moving the control there and dropping
   * this path is the follow-up, and it is a change to a screen another ticket
   * had open rather than one this ticket may make on its behalf.
   *
   * Flat, per the section's shape — see `expenses` and `stockMovements` for why
   * every screen carrying a navigation entry stays off a nested path.
   */
  itemExport: '/item-export',

  /**
   * Takings, refunds and net per payment mode over a period (#326).
   *
   * `requireRole('OWNER', 'MANAGER')`: all three routes behind it declare
   * `@RequireOutletScope()` and no role list, the same as the cash book, and
   * carry no cost or margin. One path for the park and the per-outlet read, as
   * {@link items} does.
   */
  paymentModes: '/payment-modes',

  /**
   * What a terminal took and could not deliver (FE018).
   *
   * `requireSignedIn()` and no role, which mirrors nothing in the API because
   * there is nothing there to mirror: the queue is a file on the device and this
   * screen makes no API call at all. Not advertised in the navigation either —
   * it is reached from the count in the back-office header, which appears only
   * on a terminal that has something to report.
   */
  queuedWrites: '/queued-writes',

  /**
   * Selling admission at the gate and at the activity centres (FE026).
   *
   * **The one route whose gate is not the back-office session**, and the reason
   * it is declared `publicRoute()` is argued in full on its entry in
   * `route-table.tsx`. In short: a cashier holds a PIN and a registered device,
   * not a session, so any signed-in declaration would send the till to a sign-in
   * screen it can never pass — while what actually admits somebody is the
   * device's lease and the shift running in the main process, neither of which a
   * browser can reach.
   *
   * No `nav` entry, and there must not be one: the back office and the till are
   * different navigation shells, and a link to the till in the back-office bar
   * would be a link to a screen that says "there is no till here".
   */
  till: '/till',
} as const;

/**
 * One item's price history, as a URL.
 *
 * The template and the builder sit together so a route added to the table and a
 * link written to it cannot disagree about the parameter's name — a `:id`
 * renamed with the link left interpolating the old one produces a URL that
 * matches nothing and falls through to the not-found screen.
 *
 * `encodeURIComponent` on a UUIDv7 changes nothing today. It is here because the
 * id is a value from the server rather than a literal, and a path built by
 * concatenation with no encoding is the shape that eventually carries something
 * with a slash in it.
 */
export const cataloguePricesPath = (itemId: string): string =>
  paths.cataloguePrices.replace(':id', encodeURIComponent(itemId));

/**
 * One item's availability, as a URL. Same rule as the builder above: the
 * template and the builder sit together so neither can rename `:id` alone.
 */
export const catalogueAvailabilityPath = (itemId: string): string =>
  paths.catalogueAvailability.replace(':id', encodeURIComponent(itemId));

/** One outlet's catalogue, as a URL. */
export const outletCataloguePath = (outletId: string): string =>
  paths.outletCatalogue.replace(':id', encodeURIComponent(outletId));

/** One billing machine's record, as a URL. */
export const billingMachinePath = (machineId: string): string =>
  paths.billingMachine.replace(':id', encodeURIComponent(machineId));

/**
 * One billing machine's product codes, as a URL — optionally with a code and its
 * printed name to prefill the mapping form, as an upload's `UNMAPPED` refusal
 * carries them (`detail.productCode`, `detail.productName`).
 */
export function billingMachineProductCodesPath(
  machineId: string,
  prefill?: { readonly code: string; readonly name?: string },
): string {
  const path = paths.billingMachineProductCodes.replace(':id', encodeURIComponent(machineId));
  if (prefill === undefined) return path;

  const query = new URLSearchParams({ code: prefill.code });
  if (prefill.name !== undefined) query.set('name', prefill.name);

  return `${path}?${query.toString()}`;
}

/**
 * One ticket's record, as a URL. Built from the id the server answered with —
 * never from the printed number, which is not what this route matches on.
 */
export const ticketPath = (ticketId: string): string =>
  paths.ticket.replace(':id', encodeURIComponent(ticketId));

/**
 * One sale's record, as a URL. Built from the id the server answered with —
 * **never from the receipt number**, which is not what this route matches on and
 * which is not unique anyway: two tills can print the same one, so a URL built
 * from a number would name a sale only by luck.
 */
export const salePath = (saleId: string): string =>
  paths.sale.replace(':id', encodeURIComponent(saleId));

/** One imported bill's record, as a URL. Built from the sale id, never the receipt. */
export const machineBillPath = (saleId: string): string =>
  paths.machineBill.replace(':id', encodeURIComponent(saleId));

/** One machine import's record, as a URL. */
export const machineImportPath = (importId: string): string =>
  paths.machineImport.replace(':id', encodeURIComponent(importId));

/** The bills one machine import created — the link an import's record carries. */
export const machineBillsForImportPath = (importId: string): string =>
  `${paths.machineBills}?${new URLSearchParams({ importId }).toString()}`;
